import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CardHead } from '../../design-system/composites/Card/Card';
import { ChatCircleDotsIcon } from '@phosphor-icons/react/dist/csr/ChatCircleDots';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';

import { Button } from '../../design-system/primitives/Button/Button';
import { requestsApi, type CaseRequestRow } from '../../lib/api/requests';
import { CALL_ACTION_LABEL_KEY, REQUEST_STATUS_LABEL_KEY } from '../requests/requestLabels';
import { formatTimestamp } from '../../lib/format/dateTime';
import { kindLabel } from '../requests/kindLabel';
import styles from './CaseRequests.module.css';

/**
 * The Requests panel on the case detail screen (M-09, Task 7, spec §5) —
 * beside M-08's `CaseMessages`, same shape: gated on `request.view`, its
 * own error state, no spinner that never resolves.
 *
 * ## Filtered by `case_number`, not `case_uuid` (Task 7's own brief, said here)
 *
 * The brief asked for `requestsApi.list({ case_uuid })`, adding the filter
 * to `CaseRequestController::index()` "if it is not already there". Reading
 * that controller and its `CaseRequestIndexRequest` directly in the API
 * repository: there is no `case_uuid` filter, and this task does not touch
 * the API repository at all (Tasks 6/7 are web-only). `case_number` IS
 * already a filter (`CaseRequestIndexRequest::rules()`), is unique per case
 * exactly as `case_uuid` would have been, and `CaseDetail.tsx` already holds
 * the case's own `case_number` — so this panel filters on that instead of
 * adding a route change out of this task's own scope.
 *
 * ## Read-only, deliberately
 *
 * No resend/complete/call controls here — `RequestList.tsx` (Task 6) is
 * where those live, gated on their own permissions and driven by
 * `next_action` the same way. Repeating that machinery on a second surface
 * would be a second place to keep the state-machine rendering in sync with
 * the server's `NextAction::for()`, which is exactly what the Global
 * Constraint this milestone opens with rules out. This panel answers one
 * question only: which requests exist for this case, and what has happened
 * on each so far.
 *
 * ## A panel-level fetch failure must not blank the whole case screen
 * (`CaseMessages.tsx`'s own ruling, copied here)
 *
 * `loadRequests()` sets its OWN `status`/error state, entirely separate
 * from `CaseDetail`'s. A caller who can view messages but not this case's
 * requests page, or whose request fetch fails for any other reason, still
 * sees the record, its fields, its edit/close controls and its Messages
 * panel — only this one panel shows an error, with its own retry.
 */

export interface CaseRequestsProps {
  caseNumber: string;
  canView: boolean;
  /**
   * Open one request's own detail. OPTIONAL: this panel is rendered by
   * `CaseDetail`, which is itself rendered in two places, and a caller that
   * has nowhere to put a detail surface must still be able to show the
   * list. Absent, the rows are plain text exactly as they always were —
   * never a button that looks clickable and does nothing.
   */
  onOpenRequest?: (uuid: string) => void;
}

type LoadStatus = 'loading' | 'ready' | 'error';

export function CaseRequests({ caseNumber, canView, onOpenRequest }: CaseRequestsProps) {
  const { t, i18n } = useTranslation();

  const [status, setStatus] = useState<LoadStatus>('loading');
  const [rows, setRows] = useState<readonly CaseRequestRow[] | null>(null);

  const loadRequests = useCallback(async () => {
    setStatus('loading');

    try {
      /*
       * `queue: 'all'` IS LOAD-BEARING, and its absence was a defect that
       * shipped.
       *
       * The three queues are disjoint by design — right for a work list, wrong
       * for "everything about this one case". Omitting `queue` takes the
       * server's `open` default, which means "answered but not yet dealt
       * with"; a request created a moment ago has nothing back yet and is
       * always `awaiting`. This panel therefore rendered "no requests" for
       * every case on a live system, forever, while its own tests passed on
       * fixtures that ignore the query.
       */
      const page = await requestsApi.list({
        case_number: caseNumber,
        queue: 'all',
        per_page: 200,
      });
      setRows(page.data);
      setStatus('ready');
    } catch {
      // This panel's own error state, not `CaseDetail`'s — see this
      // module's own docblock.
      setStatus('error');
    }
  }, [caseNumber]);

  useEffect(() => {
    if (!canView) return;
    void loadRequests();
  }, [canView, loadRequests]);

  if (!canView) return null;

  return (
    <section className={styles['panel']}>
      <CardHead level="h3" icon={<ChatCircleDotsIcon size={16} weight="duotone" />} title={t('requests.panelTitle')} />

      {status === 'error' ? (
        <div role="alert" className={styles['empty']}>
          <p>{t('requests.loadFailed')}</p>
          <Button
            variant="secondary"
            onClick={() => {
              void loadRequests();
            }}
            icon={<ArrowClockwiseIcon size={16} />}
          >
            {t('cases.retry')}
          </Button>
        </div>
      ) : rows === null ? (
        <p role="status">{t('list.loading')}</p>
      ) : rows.length === 0 ? (
        <p className={styles['empty']}>{t('requests.noneForCase')}</p>
      ) : (
        <ul className={styles['list']}>
          {rows.map((request) => (
            <li key={request.uuid} className={styles['row']}>
              <div className={styles['rowHeader']}>
                {/*
                  A REAL <button>, not a click handler on the row. The row is
                  a `<li>` carrying a nested list of calls; making the whole
                  thing clickable would put interactive content inside
                  interactive content and leave a keyboard user with no way
                  to reach it. The kind is the row's own name, so it is the
                  thing that opens it.
                */}
                {onOpenRequest === undefined ? (
                  <span className={styles['kind']}>
                    {kindLabel(request.kind, t) ?? t('table.notRecorded')}
                  </span>
                ) : (
                  <button
                    type="button"
                    className={styles['kindLink']}
                    onClick={() => {
                      onOpenRequest(request.uuid);
                    }}
                  >
                    {kindLabel(request.kind, t) ?? t('table.notRecorded')}
                  </button>
                )}
                <span className={styles['statusLabel']}>{t(REQUEST_STATUS_LABEL_KEY[request.status])}</span>
              </div>

              <div className={styles['meta']}>
                {request.asked_at === null ? null : (
                  <span>{t('requests.askedAt')}: <bdi dir="ltr">{formatTimestamp(request.asked_at, i18n.language)}</bdi></span>
                )}
                {request.uploaded_at === null ? null : (
                  <span>{t('requests.uploadedAt')}: <bdi dir="ltr">{formatTimestamp(request.uploaded_at, i18n.language)}</bdi></span>
                )}
                {!request.resend.exists ? null : <span>{t('requests.resendSent')}</span>}
              </div>

              {request.calls.length === 0 ? null : (
                <ul className={styles['calls']}>
                  {request.calls.map((call) => (
                    <li key={call.sequence} className={styles['call']}>
                      {t('requests.callSummary', {
                        sequence: call.sequence,
                        action: call.action === null ? '' : t(CALL_ACTION_LABEL_KEY[call.action]),
                      })}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
